import React, { useState, useEffect } from "react";
import { Card, CardContent, Grid, TextField, Select, MenuItem, FormControl, InputLabel, Button, Typography, Box, Chip, Autocomplete } from "@mui/material";
import { FilterList as FilterIcon, Clear as ClearIcon } from "@mui/icons-material";
import api from "../../services/api";

const AllocationFilterBar = ({ teacherFilter, onTeacherChange, classFilter, onClassChange, streamFilter, onStreamChange, yearFilter, onYearChange, termFilter, onTermChange, activeFilter, onActiveFilterChange, onClearFilters, hasActiveFilters, totalCount, isMobile }) => {
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [streams, setStreams] = useState([]);
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        setLoading(true);
        const [teachersRes, classesRes, termsRes] = await Promise.all([
          api.get('/report-card/teachers/active'),
          api.get('/academic/classes', { params: { per_page: 1000 } }),
          api.get('/academic/terms', { params: { per_page: 1000 } }),
        ]);
        
        setTeachers(Array.isArray(teachersRes.data) ? teachersRes.data : []);
        setClasses(Array.isArray(classesRes.data?.data) ? classesRes.data.data : Array.isArray(classesRes.data) ? classesRes.data : []);
        setTerms(Array.isArray(termsRes.data?.data) ? termsRes.data.data : Array.isArray(termsRes.data) ? termsRes.data : []);
      } catch (error) {
        console.error('Error fetching filter data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFilterData();
  }, []);

  useEffect(() => {
    if (classFilter) {
      api.get('/academic/class-streams', { params: { class_id: classFilter, per_page: 1000 } })
        .then(res => setStreams(Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : []))
        .catch(() => setStreams([]));
    } else {
      setStreams([]);
      onStreamChange("");
    }
  }, [classFilter]);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" display="flex" alignItems="center"><FilterIcon sx={{ mr: 1 }} color="primary" />Filters</Typography>
          {hasActiveFilters && <Chip label="Filters Active" color="primary" size="small" />}
        </Box>
        <Grid container spacing={2}>
          <Grid item size={{ lg: 2.2, md: 4, sm: 6, xs: 12 }}>
            <Autocomplete
              size="small"
              loading={loading}
              options={teachers}
              getOptionLabel={(t) => t.full_name || `${t.last_name} ${t.first_name}`}
              value={teachers.find(t => t.id === teacherFilter) || null}
              onChange={(_, val) => onTeacherChange(val ? val.id : '')}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={(params) => <TextField {...params} label="Teacher" />}
            />
          </Grid>
          <Grid item size={{ lg: 1.5, md: 4, sm: 6, xs: 12 }}>
            <Autocomplete
              size="small"
              loading={loading}
              options={classes}
              getOptionLabel={(c) => c.name || c.class_name || ''}
              value={classes.find(c => c.id === classFilter) || null}
              onChange={(_, val) => onClassChange(val ? val.id : '')}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={(params) => <TextField {...params} label="Class" />}
            />
          </Grid>
          <Grid item size={{ lg: 1.7, md: 4, sm: 6, xs: 12 }}>
            <Autocomplete
              size="small"
              options={streams}
              getOptionLabel={(s) => s.name || s.stream_name || ''}
              value={streams.find(s => s.id === streamFilter) || null}
              onChange={(_, val) => onStreamChange(val ? val.id : '')}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              disabled={!classFilter}
              renderInput={(params) => <TextField {...params} label="Stream" />}
            />
          </Grid>
          <Grid item size={{ lg: 1.7, md: 4, sm: 6, xs: 12 }}>
            <TextField fullWidth size="small" type="number" label="Year" value={yearFilter} onChange={(e) => onYearChange(e.target.value)} />
          </Grid>
          <Grid item size={{ lg: 1.7, md: 4, sm: 6, xs: 12 }}>
            <Autocomplete
              size="small"
              loading={loading}
              options={terms}
              getOptionLabel={(t) => t.name || t.term_name || ''}
              value={terms.find(t => t.id === termFilter) || null}
              onChange={(_, val) => onTermChange(val ? val.id : '')}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={(params) => <TextField {...params} label="Term" />}
            />
          </Grid>
          <Grid item size={{ lg: 1.4, md: 4, sm: 6, xs: 12 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={activeFilter} label="Status" onChange={(e) => onActiveFilterChange(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {hasActiveFilters && (
            <Grid item size={{ lg: 1.8, md: 4, sm: 6, xs: 12 }}>
              <Button variant="outlined" startIcon={<ClearIcon />} onClick={onClearFilters} size="small" fullWidth>Clear Filters</Button>
            </Grid>
          )}
        </Grid>
        <Box mt={2}><Typography variant="body2" color="text.secondary">Total Allocations: {totalCount}</Typography></Box>
      </CardContent>
    </Card>
  );
};

export default AllocationFilterBar;
